/**
 * =============================================================================
 * usePlatform HOOK - Platform & Device Detection
 * =============================================================================
 * 
 * Detects the user's platform (iOS, Android, or Desktop) and provides
 * helpful boolean flags for platform-specific styling and behavior.
 * 
 * USAGE:
 * ```tsx
 * const { isIOS, isAndroid, isMobile, isTouchDevice } = usePlatform();
 * 
 * // Platform-specific styling
 * <div className={cn(
 *   "base-styles",
 *   isIOS && "ios-specific-styles",
 *   isMobile && "mobile-styles"
 * )}>
 * ```
 * 
 * DETECTED PLATFORMS:
 * - ios: iPhone, iPad, iPod, or Mac with touch
 * - android: Any Android device
 * - desktop: Everything else
 * 
 * USE CASES:
 * - iOS safe area padding (notch, home indicator)
 * - Platform-specific animations
 * - Touch vs mouse interaction handling
 * - Haptic feedback (Android only)
 * 
 * @returns {PlatformInfo} Object with platform detection flags
 * 
 * @see src/components/Header.tsx - Uses for iOS safe area
 * @see src/components/Footer.tsx - Uses for iOS safe area
 * @see src/utils/haptics.ts - Uses for haptic feedback
 * =============================================================================
 */

import { useState, useEffect } from "react";

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/** Supported platform types */
export type Platform = "ios" | "android" | "desktop";

/** Platform detection result */
interface PlatformInfo {
  /** Detected platform type */
  platform: Platform;
  /** True if iOS device (iPhone, iPad, iPod, or Mac with touch) */
  isIOS: boolean;
  /** True if Android device */
  isAndroid: boolean;
  /** True if any mobile device (iOS or Android) */
  isMobile: boolean;
  /** True if desktop (not mobile) */
  isDesktop: boolean;
  /** True if device supports touch */
  isTouchDevice: boolean;
}

// =============================================================================
// MAIN HOOK
// =============================================================================

/**
 * Hook to detect the user's platform and device capabilities.
 * 
 * @returns Platform detection flags
 */
export function usePlatform(): PlatformInfo {
  // Initialize with detected platform (handles SSR)
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo>(() => 
    detectPlatform()
  );

  // Re-detect on mount (in case of hydration mismatch)
  useEffect(() => {
    setPlatformInfo(detectPlatform());
  }, []);

  return platformInfo;
}

// =============================================================================
// DETECTION LOGIC
// =============================================================================

/**
 * Detects the current platform based on user agent and browser features.
 * 
 * Detection logic:
 * 1. Check user agent for iOS/Android keywords
 * 2. Check for touch capability
 * 3. Handle iPad (which reports as Mac with touch)
 * 
 * @returns Platform detection result
 */
function detectPlatform(): PlatformInfo {
  // SSR fallback: return desktop
  if (typeof window === "undefined") {
    return {
      platform: "desktop",
      isIOS: false,
      isAndroid: false,
      isMobile: false,
      isDesktop: true,
      isTouchDevice: false,
    };
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = window.navigator.platform?.toLowerCase() || "";
  
  // ---------------------------------------------------------------------------
  // iOS Detection
  // ---------------------------------------------------------------------------
  // Standard iOS devices: iPhone, iPad, iPod
  // Modern iPad: Reports as Mac but has touch support
  const isIOS = /iphone|ipad|ipod/.test(userAgent) || 
                (platform.includes("mac") && "ontouchend" in document);
  
  // ---------------------------------------------------------------------------
  // Android Detection
  // ---------------------------------------------------------------------------
  const isAndroid = /android/.test(userAgent);
  
  // ---------------------------------------------------------------------------
  // Touch Detection
  // ---------------------------------------------------------------------------
  // Multiple checks for broad browser support
  const isTouchDevice = "ontouchstart" in window || 
                        navigator.maxTouchPoints > 0;
  
  // ---------------------------------------------------------------------------
  // Derive Other Flags
  // ---------------------------------------------------------------------------
  const isMobile = isIOS || isAndroid;
  const detectedPlatform: Platform = isIOS ? "ios" : isAndroid ? "android" : "desktop";

  return {
    platform: detectedPlatform,
    isIOS,
    isAndroid,
    isMobile,
    isDesktop: !isMobile,
    isTouchDevice,
  };
}
